// @mui
import { Box, Container, Typography, Link } from '@mui/material';
import { usePortal } from 'src/hooks/use-portal';
import { useResponsive } from 'src/hooks/use-responsive';
import Image from 'src/components/image';
import { RouterLink } from 'src/routes/components';
import { paths } from 'src/routes/paths';
import Lightbox, { useLightBox } from 'src/components/lightbox';

// ----------------------------------------------------------------------

export default function CategoryListHomeView({ isRTL }: any) {
  const { categories, items } = usePortal({});
  const mdUp = useResponsive('up', 'md');

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '50vh', md: '70vh' },
          overflow: 'hidden',
        }}
      >
        <Image
          alt="Services Hero"
          src="https://res.cloudinary.com/drceui2nh/image/upload/v1768447533/1500x0_15_l1dbhc.jpg"
          sx={{
            width: '100%',
            height: '100%',
            '& img': {
              objectFit: 'cover',
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.3)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            zIndex: 10,
            width: '100%',
            px: 2,
          }}
        >
          <Typography
            variant="h1"
            sx={{
              color: 'common.white',
              fontWeight: 200,
              letterSpacing: { xs: 4, md: 12 },
              fontSize: { xs: 32, md: 56 },
              textTransform: 'uppercase',
            }}
          >
            {isRTL ? 'الفئات' : 'Services'}
          </Typography>
        </Box>
      </Box>

      {/* Content Section */}
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="xl">
          {/* Categories with Items */}
          {categories.map((category: any, catIndex: number) => {
            const categoryItems = items.filter((item: any) => item.categoryId === category._id);
            if (categoryItems.length === 0) return null;

            return (
              <CategoryBlock
                key={category._id}
                category={category}
                items={categoryItems}
                isRTL={isRTL}
                mdUp={mdUp}
                index={catIndex}
              />
            );
          })}
        </Container>
      </Box>
    </>
  );
}

// ----------------------------------------------------------------------
// CATEGORY BLOCK COMPONENT
// ----------------------------------------------------------------------

type CategoryBlockProps = {
  category: any;
  items: any[];
  isRTL: boolean;
  mdUp: boolean;
  index: number;
};

function CategoryBlock({ category, items, isRTL, mdUp, index }: CategoryBlockProps) {
  const title = isRTL ? category.nameAr : category.name;
  const desc = isRTL ? category.desc || '' : category.desc || '';

  // Lightbox for all items
  const displayedItems = items.slice(0, mdUp ? 8 : 4);
  const allImages = displayedItems.map((item: any) => ({
    src: item.photo,
    alt: isRTL ? item.nameAr : item.name,
  }));

  const lightbox = useLightBox(allImages);

  return (
    <Box sx={{ mb: { xs: 8, md: 12 } }}>
      {/* Category Header */}
      <Box sx={{ mb: { xs: 4, md: 6 }, textAlign: index % 2 === 0 ? 'left' : 'right' }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 300,
            letterSpacing: 4,
            textTransform: 'uppercase',
            mb: 2,
            fontSize: { xs: 28, md: 42 },
          }}
        >
          {title}
        </Typography>
        {desc && (
          <Typography
            sx={{
              color: 'text.secondary',
              maxWidth: 600,
              mx: index % 2 === 0 ? 0 : 'auto',
              ml: index % 2 === 0 ? 0 : 'auto',
              fontWeight: 300,
              fontSize: { xs: 14, md: 16 },
              lineHeight: 1.8,
            }}
          >
            {desc}
          </Typography>
        )}
      </Box>

      {/* Items Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: { xs: 1, md: 2 },
        }}
      >
        {displayedItems.map((item: any, idx: number) => (
          <Box
            key={item._id}
            onClick={() => lightbox.onOpen(item.photo)}
            sx={{
              position: 'relative',
              aspectRatio: '1/1',
              overflow: 'hidden',
              cursor: 'pointer',
              textDecoration: 'none',
              '&:hover': {
                '& .overlay': {
                  opacity: 1,
                },
                '& img': {
                  transform: 'scale(1.05)',
                },
              },
            }}
          >
            <Image
              alt={isRTL ? item.nameAr : item.name}
              src={item.photo}
              sx={{
                width: '100%',
                height: '100%',
                transition: 'transform 0.3s ease',
                '& img': {
                  objectFit: 'cover',
                },
              }}
            />
            <Box
              className="overlay"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(0,0,0,0.3)',
                opacity: 0,
                transition: 'opacity 0.3s ease',
              }}
            />
          </Box>
        ))}
      </Box>

      {/* Lightbox */}
      <Lightbox
        index={lightbox.selected}
        slides={allImages}
        open={lightbox.open}
        close={lightbox.onClose}
      />

      {/* View All Link */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Link
          component={RouterLink}
          href={paths.category.details(category._id)}
          sx={{
            color: 'text.primary',
            textDecoration: 'none',
            textTransform: 'uppercase',
            letterSpacing: 3,
            fontSize: 12,
            fontWeight: 500,
            borderBottom: '1px solid',
            borderColor: 'text.primary',
            pb: 0.5,
            transition: 'opacity 0.3s ease',
            '&:hover': {
              opacity: 0.6,
            },
          }}
        >
          {isRTL ? 'عرض الكل' : 'VIEW ALL'}
        </Link>
      </Box>
    </Box>
  );
}
